import React, { useState } from "react";
import {
  Box,
  Flex,
  // ListItem,
  Radio,
  RadioGroup,
  Text,
  // UnorderedList,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
// import HelpTooltip from "@/shared/components/help-tooltip";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideDisabledHint from "@/features/pathpick/features/start-guide/components/PathPickStartGuideDisabledHint";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  START_GUIDE_SALES_MODES,
  StartGuideSalesMode,
} from "@/features/pathpick/features/start-guide/types";

interface Props {
  // 아직 저장한 판매 방식이 없으면 null — 그때는 아무것도 고르지 않은 채로 시작한다.
  initialSalesMode: StartGuideSalesMode | null;
  // 저장은 `다음 단계 확인하기` 를 눌렀을 때 한 번만 일어난다.
  onSubmit: (salesMode: StartGuideSalesMode) => void;
}

const HINT_ID = "start-guide-selling-profile-hint";

// 기획이 정한 노출 순서 — 설정이 필요한 스마트스토어 두 방식을 먼저 둔다.
const SALES_MODE_ORDER: StartGuideSalesMode[] = ["api", "file", "kream"];

const SALES_MODE_LABEL_KEYS: Record<StartGuideSalesMode, string> = {
  kream: "path_pick_guide:selling_profile.mode_kream",
  api: "path_pick_guide:selling_profile.mode_api",
  file: "path_pick_guide:selling_profile.mode_file",
};

// // 도움말 툴팁 본문 — 라디오 라벨만으로는 각 방식이 실제로 무엇을 하는지 드러나지 않아
// // 세 방식을 한자리에서 비교할 수 있게 적는다. KREAM만 두 번째 줄에 파일 2종이 딸린다.
// const HELP_SECTIONS: {
//   mode: StartGuideSalesMode;
//   items: { key: string; subKeys?: string[] }[];
// }[] = [
//   { mode: "api", items: [{ key: "api_1" }, { key: "api_2" }] },
//   {
//     mode: "file",
//     items: [{ key: "file_1" }, { key: "file_2" }, { key: "file_3" }],
//   },
//   {
//     mode: "kream",
//     items: [
//       { key: "kream_1" },
//       { key: "kream_2", subKeys: ["kream_2_1", "kream_2_2"] },
//       { key: "kream_3" },
//     ],
//   },
// ];

const isSalesMode = (value: string): value is StartGuideSalesMode =>
  (START_GUIDE_SALES_MODES as readonly string[]).includes(value);

/**
 * 판매 방식 선택 — 가이드의 첫 화면이자, 이후 설정 목록·작업 패널·신청 후 이용의 분기를 정하는 곳.
 *
 * 셋 중 하나만 고른다. 예전에는 채널 중복 선택이 가능했지만 두 채널을 함께 그리는 화면이
 * 애초에 없어서, 둘 다 고르면 스마트스토어 안내만 나오고 KREAM 안내는 영영 보이지 않았다.
 * 고르지 않고는 넘어갈 수 없다 — 이 선택이 뒤 단계의 내용을 통째로 바꾸기 때문이다.
 */
function PathPickStartGuideSellingProfileSection({
  initialSalesMode,
  onSubmit,
}: Props) {
  const { t } = useI18nHelper();
  // 편집 중에는 저장하지 않는다 — 고르기만 하고 나가면 기존 판매 방식이 그대로 남아야 한다.
  const [salesMode, setSalesMode] = useState<StartGuideSalesMode | null>(
    initialSalesMode
  );

  return (
    <PathPickStartGuideCard
      header={
        <Flex flexDirection={"column"} gap={"8px"}>
          <Text textStyle={"body-lg-bold"} fontSize={"24px"} color={"#1D1D1B"}>
            {t("path_pick_guide:selling_profile.card_title")}
          </Text>
          <Text
            textStyle={"body-sm-medium"}
            color={"#6F6F6C"}
            fontSize={"14px"}
          >
            {t("path_pick_guide:selling_profile.card_description")}
          </Text>
        </Flex>
      }
      headerProps={{
        paddingBottom: "0px",
      }}
    >
      <Flex flexDirection={"column"} gap={"16px"}>
        <Box as={"fieldset"}>
          {/* 도움말 아이콘(현재 주석)은 legend 안에 들어간다 — legend는 fieldset의 첫 자식이어야 한다. */}
          <Box
            as={"legend"}
            display={"flex"}
            alignItems={"center"}
            gap={"4px"}
            marginBottom={"12px"}
          >
            <Text textStyle={"body-sm-bold"} color={"#22221F"}>
              {t("path_pick_guide:selling_profile.mode_group_label")}
            </Text>
            {/* <SalesModeHelpTooltip /> */}
          </Box>
          <RadioGroup
            value={salesMode ?? ""}
            onChange={(value) => {
              if (isSalesMode(value)) {
                setSalesMode(value);
              }
            }}
          >
            <Flex flexDirection={"column"} gap={"12px"}>
              {SALES_MODE_ORDER.map((mode) => (
                <Radio key={mode} value={mode}>
                  <Text textStyle={"body-sm-medium"} color={"#22221F"}>
                    {t(SALES_MODE_LABEL_KEYS[mode])}
                  </Text>
                </Radio>
              ))}
            </Flex>
          </RadioGroup>
        </Box>

        <Box
          padding={"12px 14px"}
          borderRadius={"6px"}
          backgroundColor={"#F7F7F5"}
        >
          <Text textStyle={"caption-medium"} color={"#6F6F6C"}>
            {t("path_pick_guide:selling_profile.footnote")}
          </Text>
        </Box>

        <Flex flexDirection={"column"} gap={"6px"} alignItems={"flex-start"}>
          <AppButton
            variant={"primary"}
            isDisabled={salesMode === null}
            onClick={() => salesMode && onSubmit(salesMode)}
            chakraProps={
              salesMode === null ? { "aria-describedby": HINT_ID } : undefined
            }
          >
            {t("path_pick_guide:selling_profile.submit")}
          </AppButton>
          {salesMode === null && (
            <PathPickStartGuideDisabledHint id={HINT_ID}>
              {t("path_pick_guide:selling_profile.submit_disabled_hint")}
            </PathPickStartGuideDisabledHint>
          )}
        </Flex>
      </Flex>
    </PathPickStartGuideCard>
  );
}

// /**
//  * 판매 방식 설명 툴팁 — 이 화면에서만 쓰는 표현이라 같은 파일에 둔다.
//  *
//  * 툴팁 내용이 문자열이 아니라 목록 마크업이므로, 스크린리더용 aria-label을 따로 준다
//  * (Chakra Tooltip 규약).
//  */
// function SalesModeHelpTooltip() {
//   const { t } = useI18nHelper();
//   const tHelp = (key: string) =>
//     t(`path_pick_guide:selling_profile.mode_tooltip.${key}`);
//
//   return (
//     <HelpTooltip
//       placement={"right"}
//       maxWidth={"360px"}
//       aria-label={tHelp("aria_label")}
//       text={
//         <Flex flexDirection={"column"} gap={"8px"} paddingY={"2px"}>
//           {HELP_SECTIONS.map(({ mode, items }) => (
//             <Box key={mode}>
//               <Text textStyle={"body-sm-bold"}>{tHelp(`${mode}_title`)}</Text>
//               <UnorderedList spacing={"2px"} paddingLeft={"16px"}>
//                 {items.map(({ key, subKeys }) => (
//                   <ListItem key={key}>
//                     <Text textStyle={"body-sm-medium"}>{tHelp(key)}</Text>
//                     {subKeys && (
//                       <UnorderedList
//                         styleType={"circle"}
//                         spacing={"2px"}
//                         paddingLeft={"14px"}
//                       >
//                         {subKeys.map((subKey) => (
//                           <ListItem key={subKey}>
//                             <Text textStyle={"body-sm-medium"}>
//                               {tHelp(subKey)}
//                             </Text>
//                           </ListItem>
//                         ))}
//                       </UnorderedList>
//                     )}
//                   </ListItem>
//                 ))}
//               </UnorderedList>
//             </Box>
//           ))}
//         </Flex>
//       }
//     />
//   );
// }

export default PathPickStartGuideSellingProfileSection;
